/**
 * Tile-layer configuration for the field map, behind one swappable seam.
 *
 * Default is OpenStreetMap — free, no key — so the map works out of the box in
 * dev and self-hosted. Once the company has a Mapbox account, set
 * `VITE_MAPBOX_TOKEN` (and optionally `VITE_MAPBOX_STYLE`) and the same Leaflet
 * `TileLayer` renders Mapbox raster tiles instead — no component changes.
 */
export interface TileConfig {
  url: string;
  attribution: string;
  /** Mapbox styles go up to 22; OSM raster tops out at 19. */
  maxZoom: number;
}

const OSM: TileConfig = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap contributors',
  maxZoom: 19,
};

function mapbox(token: string): TileConfig {
  // Default to streets-v12; override with VITE_MAPBOX_STYLE (e.g. "light-v11").
  const style = import.meta.env.VITE_MAPBOX_STYLE ?? 'streets-v12';
  return {
    url:
      `https://api.mapbox.com/styles/v1/mapbox/${style}/tiles/256/{z}/{x}/{y}@2x` +
      `?access_token=${token}`,
    attribution: '&copy; Mapbox &copy; OpenStreetMap contributors',
    maxZoom: 22,
  };
}

/** Resolve the active tile config from the environment. */
export function getTileConfig(): TileConfig {
  const token = import.meta.env.VITE_MAPBOX_TOKEN;
  return token ? mapbox(token) : OSM;
}

/**
 * New Brunswick, edge to edge — the opening view when there are no files to fit
 * and the active municipality could not be located. The province is the one
 * extent that is right for every account; any single town is wrong for most.
 */
export const NB_BOUNDS: [[number, number], [number, number]] = [
  [44.6, -69.06],
  [48.07, -63.77],
];

/** Close enough to read a municipality's streets, wide enough to hold its edges. */
export const MUNICIPALITY_ZOOM = 12;
